// URL ⇄ filter state sync. The URL is the source of truth so links are
// shareable; the React state mirrors it.

import { useMemo, useCallback } from "react";
import type {
  ConversationFilters,
  ConversationKind,
  ConversationVisibility,
  ParticipantsOp,
} from "./types";
import { DEFAULT_FILTERS } from "./types";

function parseCsv(v: string | null): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseKinds(v: string | null): ConversationKind[] {
  const valid: ConversationKind[] = [
    "meeting",
    "slack_channel",
    "slack_dm",
    "slack_group_dm",
    "email_thread",
    "calendar_event",
  ];
  return parseCsv(v).filter((k): k is ConversationKind =>
    valid.includes(k as ConversationKind),
  );
}

function parseVisibility(v: string | null): ConversationVisibility[] {
  const valid: ConversationVisibility[] = [
    "personal",
    "work_private",
    "work_shared",
    "public",
  ];
  const parsed = parseCsv(v).filter((x): x is ConversationVisibility =>
    valid.includes(x as ConversationVisibility),
  );
  return parsed.length > 0 ? parsed : DEFAULT_FILTERS.visibility;
}

export function useFiltersFromUrl(
  params: URLSearchParams,
  setParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void,
) {
  const filters: ConversationFilters = useMemo(() => {
    return {
      participants: parseCsv(params.get("with")),
      op: (params.get("op") as ParticipantsOp) === "or" ? "or" : "and",
      kinds: parseKinds(params.get("kind")),
      since: params.get("since") ?? DEFAULT_FILTERS.since,
      until: params.get("until"),
      visibility: parseVisibility(params.get("visibility")),
      q: params.get("q"),
    };
  }, [params]);

  const setFilter = useCallback(
    <K extends keyof ConversationFilters>(
      key: K,
      value: ConversationFilters[K],
    ) => {
      const next = new URLSearchParams(params);
      const stripIf = (k: string, cond: boolean) => {
        if (cond) next.delete(k);
      };

      switch (key) {
        case "participants": {
          const arr = value as string[];
          if (arr.length === 0) next.delete("with");
          else next.set("with", arr.join(","));
          break;
        }
        case "op":
          stripIf("op", value === "and");
          if (value !== "and") next.set("op", value as string);
          break;
        case "kinds": {
          const arr = value as ConversationKind[];
          if (arr.length === 0) next.delete("kind");
          else next.set("kind", arr.join(","));
          break;
        }
        case "since":
        case "until":
        case "q":
          if (value === null || value === "") next.delete(key as string);
          else next.set(key as string, String(value));
          break;
        case "visibility": {
          const arr = value as ConversationVisibility[];
          const isDefault =
            arr.length === DEFAULT_FILTERS.visibility.length &&
            DEFAULT_FILTERS.visibility.every((v) => arr.includes(v));
          if (isDefault) next.delete("visibility");
          else next.set("visibility", arr.join(","));
          break;
        }
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  return { filters, setFilter };
}
